import { lazy, ComponentType } from "react";

/**
 * Wrapper around React.lazy that retries failed chunk loads.
 * On mobile networks, JS chunks can fail to download, causing
 * the ErrorBoundary to show "Something went wrong".
 * This retries up to 3 times with increasing delay, then
 * forces a full page reload as a last resort (once per session).
 */
export function lazyRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  retries = 3
) {
  return lazy(() => retryImport(factory, retries));
}

async function retryImport<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  retries: number
): Promise<{ default: T }> {
  try {
    return await factory();
  } catch (error) {
    if (retries <= 0) {
      // Last resort: reload once per session to get fresh chunk manifest
      const reloadKey = "chunk-reload-" + window.location.pathname;
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
      }
      throw error;
    }

    // Wait before retrying (200ms, 600ms, 1800ms)
    await new Promise((r) => setTimeout(r, (4 - retries) * 200 * (4 - retries)));
    return retryImport(factory, retries - 1);
  }
}
